import React, { useEffect, useRef, useState } from 'react'
import { useContentStore, useGlobalStore, useProductStore, useBasketStore } from '../../store/hooks/useStore'
import {BaseButton, Box, Container, Flex, Stack, Text} from '../../vanilla'
import { useMealDeals } from './use-meal-deals'
import { useMealDealsCompleteness } from './use-meal-deal-completeness'
import { usePageDataTracking } from '../../analytics/page-tracking'
import { scaleDownFadeOutKeyFrame, scaleUpFadeInKeyFrame, stickyBannerContainer } from '../../components/cms/sticky-banner/styles.css'
import { Spinner } from '../../components/spinner'
import { Layout } from '../../components/layout'
import { Slot } from '../../components/cms/Slot'
import { SlotItem } from '../../types/cms'
import { CompletenessMessage } from './completeness-message'
import { MealDealItem } from './meal-deals-item'
import { Link } from 'react-router-dom'
import {Progress} from '../../types/meal-deals'
import {useMealDealTracking} from '../../analytics/meal-deal-tracking'
import {useMealDealsSelectionCount} from './use-meal-deals-selection-count'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import {Nullable} from '../../types/utils'
import { flowResult } from 'mobx'
import { enoughProductsInPromotionGroup, filteredProductsIds } from '../../utils/product-utils'
import { observer } from 'mobx-react'
import { StatusMap } from '../../types/commerce'

const animationMap = {
  show: scaleUpFadeInKeyFrame,
  hide: scaleDownFadeOutKeyFrame,
} as const

export const MealDealBuilder = observer(() => {

  const {mealDeals, dealStatus} = useMealDeals()
  const {complete, update} = useMealDealsCompleteness(mealDeals?.groups)
  const [animation, setAnimation] = useState<keyof typeof animationMap>('show')
  const {productsById, loading} = useProductStore()
  const redirectShoppingUrl ='/offers'
  const [isLoading, setIsLoading] = useState(true)
  const sendPageData = usePageDataTracking()
  const sendMealDealData = useMealDealTracking()
  const pageTracked = useRef(false)
  const [mealDealBanner, setMealDealBanner] = useState<Nullable<DefaultContentBody>>(null)
  const [builderAvailable, setBuilderAvailable] = useState<boolean>(false)
  const {contentById, fetchItemByKey} = useContentStore()
  const {selectedPriceDayOffset, selectedRegion} = useGlobalStore()
  const {basket, status: basketStatus} = useBasketStore()
  const BUILDER_PAGE_TYPE = 'Meal Deal Builder'

  useEffect(() => {
    if (dealStatus !== 'done' || loading) return

    async function setupMealDeal() {
      if (mealDeals && !mealDealBanner && isLoading) {
        await flowResult(fetchItemByKey(mealDeals?.id))
        setMealDealBanner(contentById[mealDeals?.id] || null)
      }

      // Only proceed to availability filtering if we have basket data
      if (basketStatus != StatusMap.IDLE) return

      const todaysDate = new Date()
      const startDate = mealDeals?.startDate ? new Date(mealDeals.startDate) : null
      const endDate = mealDeals?.endDate ? new Date(mealDeals.endDate) : null

      const validStartDate = !startDate || todaysDate >= startDate
      const validEndDate = !endDate || todaysDate < endDate

      const mealDealResponseIsValid =
        mealDeals != null &&
        mealDeals.c_isMealDealLanding === true &&
        mealDeals.c_customerIsEligible === true

      const dealHasValidProducts =
        Object.keys(productsById).length > 0 &&
        enoughProductsInPromotionGroup({
          promotion: mealDeals,
          productsIds: null,
          selectedPriceDayOffset: selectedPriceDayOffset ?? null,
          selectedRegion,
          productsById
        }) === true

      setBuilderAvailable(
        mealDealResponseIsValid &&
        dealHasValidProducts &&
        validStartDate &&
        validEndDate
      )

      setIsLoading(false)
    }

    setupMealDeal()
  }, [
    dealStatus,
    loading,
    mealDeals,
    mealDealBanner,
    fetchItemByKey,
    contentById,
    isLoading,
    basket?.basketId,
    selectedRegion,
    selectedPriceDayOffset,
    productsById,
    basketStatus
  ])

  // Hide spinner when mealDeals has loaded
  useEffect(() => {
    if (!isLoading) {
      // Action Tracking
      if (!pageTracked.current) {
        pageTracked.current = true
        sendPageData(BUILDER_PAGE_TYPE, (builderAvailable ? 'Available' : 'Unavailable'))
      }
    }
  }, [mealDeals, builderAvailable, loading, dealStatus, productsById, sendPageData, BUILDER_PAGE_TYPE])

  useEffect(() => {
    if (complete) {
      setAnimation('show')
    } else {
      setAnimation('hide')
    }
  }, [complete])

  const allGroupProductIds = mealDeals?.groups?.flatMap(group => group.ids) ?? []
  const totalSelection = useMealDealsSelectionCount(allGroupProductIds)
  const hasAnySelection = totalSelection > 0

  let progress: Progress = Progress.NotStarted
  if (complete) {
    progress = Progress.Completed
  } else if (hasAnySelection) {
    progress = Progress.InProgress
  } else {
    progress = Progress.NotStarted
  }

  const sentProgressRef = useRef<Progress | null>(null)

  useEffect(() => {
    if (dealStatus !== 'done' || allGroupProductIds.length === 0 || sentProgressRef.current === progress) return
    sendMealDealData({
      id: mealDeals?.id,
      name: mealDeals?.name,
      progress,
    })
  }, [dealStatus, hasAnySelection, progress])

  if (isLoading) {
    return (
      <Flex justify="center" align="center" style={{minHeight: '200px'}}>
        <Spinner color="primary" />
      </Flex>
    )
  }

  if (!builderAvailable) {
    return (
      <Layout>
        <Container paddingX={['0px', '8px']} paddingY="16px">
          <Flex justifyContent="center">
            <Box bg="white" padding="32px" maxWidth="half">
              <Stack gap="16px">
                <Text variant="heading4" lineHeight={'taller'}>Sorry, this deal isn’t available right now</Text>
                <Text variant="text4">It could be out of stock or no longer available in your store, but we have plenty more deals we think you’ll love.</Text>
                <Text variant="text4">
                  Don't worry, we have loads of other great deals available in your area!
                </Text>
                <BaseButton as={Link} to={redirectShoppingUrl}>
                  <Text variant="text4">See more offers</Text>
                </BaseButton>
              </Stack>
            </Box>
          </Flex>
        </Container>
      </Layout>
    )
  }

  return (
    <>
      <Layout>
        <Container paddingX={['0px', '8px']} paddingY="16px">
        <Box display={'block'} paddingBottom={'20px'}>
          {mealDealBanner && (
            <Slot
              data={mealDealBanner as SlotItem}
              deliveryKey={mealDealBanner._meta.deliveryKey}
            />
          )}
        </Box>
          <Stack gap="48px" id={mealDeals?.id}>
            {complete ? <CompletenessMessage /> : null}
            {mealDeals?.groups?.map?.((group, index) => (
              <MealDealItem
                index={index}
                key={`${mealDeals?.id}-${group.label}`}
                label={group.label}
                productsIds={filteredProductsIds(
                  {
                    promotion: mealDeals,
                    productsIds: group?.ids,
                    selectedPriceDayOffset: selectedPriceDayOffset ?? null,
                    selectedRegion: selectedRegion,
                    productsById: productsById
                  }
                )}
                req={group.req}
                setCompleteness={(complete) => {
                  update(index, complete)
                }}
              />
            ))}
            <Box>
              <Flex gap="16px" justifyContent="center">
                <BaseButton to="/basket" as={Link} variant="outlineDark">
                  <Text>View Basket</Text>
                </BaseButton>
                <BaseButton as={Link} to="/" data-test-selector="continue-shopping">
                  <Text>Continue Shopping</Text>
                </BaseButton>
              </Flex>
            </Box>
            {mealDeals?.calloutMsg ? (
              <Box>
                <Text color="gray500">{mealDeals?.calloutMsg}</Text>
              </Box>
            ) : null}
          </Stack>
        </Container>
      </Layout>
      {complete ? (
        <Box
          className={stickyBannerContainer}
          position="fixed"
          zIndex="sticky"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            animationName: animationMap[animation],
          }}
        >
          <CompletenessMessage
            onClose={() => {
              setAnimation('hide')
            }}
            showCloseIcon
          />
        </Box>
      ) : null}
    </>
  )
  
})